import { DataConverter } from "../../Global/Base/Converters/DataConverter";
import { CheckBoxUtils } from "../UI.ElementarControls/Helpers/CheckBoxUtils";
import { createElement, registerCustomElement } from "../UI.ElementarControls/Utils/CustomElementUtils";
import { Control } from "./Control";
import { EditableControl } from "./EditableControl";
import { IBooleanDetailValueEvent } from "./IEvent";

export class Checkbox extends EditableControl {
    protected override _innerElement: HTMLInputElement;

    private _label: HTMLLabelElement;
    private _isNullable: boolean;

    //#region Properties
    protected static override get observedAttributes(): string[] {
        return ["text"].concat(EditableControl.observedAttributes);
    }

    public set isNullable(v: boolean) {
        this._isNullable = v;
    }

    /**
     * Informace o tom, zda muze byt 3 stavovy, pokud je true, je 3 stavovy
     */
    public get isNullable(): boolean {
        return this._isNullable;
    }

    /** Informace o tom, zda se jedná o prázdný 3 stavový check (null) */
    public get indeterminate(): boolean {
        return this._innerElement.indeterminate;
    }

    /**
     * Pokud je true tak se pouziva checkbox ve standartnim modu (true - fajfka, false - prazdny)
     */
    public set standardAction(v: boolean) {
        this.classList.toggle("CheckBox2s", v);
    }

    public get standardAction(): boolean {
        return this.classList.contains("CheckBox2s");
    }

    public override set value(val: any) {
        if (typeof val === "string")
            this.controlValue = DataConverter.toBoolean(val, null);
        this.controlValue = val;
    }

    public override get value(): any {
        return this.controlValue;
    }

    public set checked(v: boolean) {
        const oldValue = this.controlValue;
        this.controlValue = v;
        if (oldValue !== v) {
            this.callEvent("change");
        }
    }

    public get checked(): boolean {
        return this._innerElement.checked;
    }

    //#region controlValue
    public get controlValue(): boolean | null {
        if (this.isNullable && this._innerElement.indeterminate) {
            return null;
        }

        return this._innerElement.checked;
    }

    /**
     * Nastavi hodnotu editu
     */
    public set controlValue(value: boolean | null) {
        CheckBoxUtils.setCheckValue(this._innerElement, value);
        if (value !== null) {
            this._innerElement.checked = value;
        }
    }
    //#endregion controlValue

    public get text(): string {
        return this._label.textContent;
    }

    public set text(text: string) {
        this._label.textContent = text;
    }

    public setTabIndexOnInnerElement(tabIndex: number): void {
        this._innerElement.tabIndex = tabIndex;
    }
    //#endregion Properties

    protected override _constructor(): void {
        super._constructor();

        this.classList.remove("EditableControl");
        this.classList.add("CheckBox");
        this._isNullable = (this.hasAttribute("displayformat") && this.getAttribute("displayformat") == "2s") ? false : true;
        this.innerHTML = "";

        this._innerElement = createElement({ type: "input[checkbox]" }) as HTMLInputElement;
        this.appendChild(this._innerElement);
        this._innerElement.id = Control.getID();

        // error elem
        this._errorElement = document.createElement("div");
        this._errorElement.className = "errDiv";

        this.isEditing = true;

        this._label = document.createElement("label");
        this.appendChild(this._label);
    }

    protected override connectedCallback(): void {
        super.connectedCallback();

        const handleClick = (_event: MouseEvent) => {
            if (this._innerElement.readOnly || !this.enabled) {
                return;
            }

            const target = _event.target;
            const isTargetInput = target instanceof Element && target.nodeName.toLowerCase() === "input";
            // pokud se event spusti kvuli inputu, tak se controlValue změní dřív, než se sem dostaneme
            const controlValue = isTargetInput ? !this.controlValue : this.controlValue;
            switch (controlValue) {
                case false:
                    if (this.required || !this._isNullable) {
                        this.controlValue = true;
                    } else {
                        this.controlValue = null;
                    }
                    break;

                // checked na unchecked
                case true:
                    this.controlValue = false;
                    break;

                // indeterminate na checked
                default:
                    this.controlValue = true;
            }

            this.callEvent("change");
        };

        Control.registerEventListener(this._eventGroup, this._label, "click", handleClick);
        Control.registerEventListener(this._eventGroup, this._innerElement, "click", handleClick);

        this.registerEventListener("enabled", (e: IBooleanDetailValueEvent) => {
            this.classList.toggle("Disabled", !e.detail.value);
        });
        this.classList.toggle("Disabled", !this.enabled);
    }

    protected override attributeChangedCallback(attr: string, _oldVal: string, newVal: string): void {
        // zesynchronizování atributu s property
        switch (attr) {
            case "text":
                this.text = newVal;
                break;
        }
    }
}

registerCustomElement("tsc-checkbox", Checkbox);
